import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, Heading, Spinner, Text, Select, Button, Input, useToast, Checkbox, CheckboxGroup, Stack } from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import Axios from '../utils/httpClient';
import { kpiSections } from './kpiData';
import Layout from './Layout';

// Updated to include all parameters that should be handled through kafedra scoring
const PARAM_CODES = ['1.2','1.3','1.5','2.1','2.2','2.3','3.1','3.2','4.7'];

const KafedraTeachers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [kafedra, setKafedra] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState({});
  const [points, setPoints] = useState({});
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const paramDefs = useMemo(() => {
    const all = kpiSections.flatMap(s => s.items);
    return PARAM_CODES.map(code => all.find(i => String(i.no) === code)).filter(Boolean);
  }, []);

  const load = () => {
    setLoading(true);
    Axios().get(`api/v1/account/kafedralar/${id}/teachers/`)
      .then(res => { setItems(res?.data?.teachers || []); setKafedra(res?.data?.kafedra || null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const toggleTeacherSelection = (teacherId, parameterCode) => {
    const key = `${teacherId}_${parameterCode}`;
    const isCurrentlySelected = selectedTeachers[key];
    
    setSelectedTeachers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    
    // Auto-assign max points when selecting (checking the checkbox)
    if (!isCurrentlySelected) {
      // Find the parameter definition to get max ball
      const param = paramDefs.find(p => String(p.no) === parameterCode);
      if (param) {
        const maxBall = parseInt(String(param.maxBall || '').replace(/\D/g, '') || '5', 10);
        setPoints(prev => ({
          ...prev,
          [key]: maxBall.toString()
        }));
      }
    } else {
      // Clear points when deselecting
      setPoints(prev => {
        const newPoints = { ...prev };
        delete newPoints[key];
        return newPoints;
      });
    }
  };

  const handlePointChange = (teacherId, parameterCode, value) => {
    const key = `${teacherId}_${parameterCode}`;
    setPoints(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const givePoints = () => {
    // Get all selected teacher-parameter combinations
    const selected = Object.entries(selectedTeachers)
      .filter(([key, isSelected]) => isSelected)
      .map(([key]) => key);
      
    if (selected.length === 0) {
      toast({ title: 'Hech kim tanlanmadi', status: 'warning' });
      return;
    }

    setSubmitting(true);
    
    // Prepare assignments for batch submission
    const assignments = selected.map(key => {
      const [teacherId, parameterCode] = key.split('_');
      const ball = parseInt(points[key] || 0, 10);
      return {
        teacher_id: teacherId,
        ball: ball,
        parameter_code: parameterCode
      };
    }).filter(assignment => assignment.ball > 0); // Only include valid assignments
    
    if (assignments.length === 0) {
      toast({ title: 'Hech qanday ball berilmadi', status: 'warning' });
      setSubmitting(false);
      return;
    }

    // Submit batch request
    Axios().post('api/v1/account/kafedra/batch-give-point/', { assignments })
      .then(() => {
        toast({ title: 'Ballar berildi', status: 'success', duration: 2000, position: 'top' });
        // Reset selections
        setSelectedTeachers({});
        setPoints({});
      })
      .catch(e => toast({ title: e?.response?.data?.detail || 'Xatolik', status: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return (
    <Layout>
      <Box p={4}>
      <Heading size='md' mb={3}>Kafedra: {kafedra?.name || ''}</Heading>
      <Box display='flex' gap={3} alignItems='center' mb={3}>
        <Button
          size='sm'
          leftIcon={<ArrowBackIcon />}
          bg='#133654'
          color='white'
          borderRadius='10px'
          _hover={{ bg: '#0f2a42' }}
          onClick={() => navigate('/kafedralar')}
        >
          Orqaga
        </Button>
        <Input
          placeholder="O'qituvchi bo'yicha qidirish"
          value={query}
          onChange={e => setQuery(e.target.value)}
          size='sm'
          maxW='360px'
          bg='white'
          ml='auto'
        />
      </Box>
      {loading ? <Spinner /> : (
        <Box display='grid' gridTemplateColumns='1fr' gap={8}>
          {items.filter(t => String(t.teacher_name||'').toLowerCase().includes(query.toLowerCase())).map(t => (
            <Card key={t.id} p={4}>
              <Text fontWeight={600} mb={2}>{t.teacher_name}</Text>
              <Box display='grid' gridTemplateColumns='1fr' gap={6}>
                {paramDefs.map(p => {
                  const key = `${t.teacher_id}_${p.no}`;
                  return (
                    <Card key={p.no} p={3}>
                      <Text fontWeight={600}>{p.no} — {p.parameter}</Text>
                      <Text mt={2} fontSize='sm' whiteSpace='pre-wrap'>{p.mechanism}</Text>
                      <Text mt={1} fontSize='sm'><b>Maks. ball:</b> {p.maxBall}</Text>
                      <Box display='flex' alignItems='center' gap={3} mt={3}>
                        <Checkbox 
                          isChecked={selectedTeachers[key] || false}
                          onChange={() => toggleTeacherSelection(t.teacher_id, p.no)}
                        >
                          Tanlash
                        </Checkbox>
                        {selectedTeachers[key] && (
                          <Text fontSize='sm' fontWeight='600' color='green.600'>
                            ✓ {points[key]} ball
                          </Text>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Card>
          ))}
          {!items.length ? <Text>O'qituvchilar topilmadi.</Text> : null}
          
          {/* Add submit button at the bottom if any teachers are selected */}
          {Object.values(selectedTeachers).some(selected => selected) && (
            <Box mt={4} textAlign='right'>
              <Button 
                colorScheme='whatsapp' 
                onClick={givePoints}
                isLoading={submitting}
              >
                Tanlanganlarga ball berish
              </Button>
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Layout>
  );
};

export default KafedraTeachers;